import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'store/redux.store'
import { useTranslation } from 'react-i18next'
import { getNameInitials } from 'utils/string'
import { useDispatch } from 'react-redux'
import request from '@hotmart/request'
import Cookies from 'js-cookie'
import { CATEGORY, DataLayer, EVENT, GoogleAnalyticsDataLayer } from 'src/utils/google-analytics'
import { userSlice, USER_REQUEST_IDENTIFIERS } from 'src/modules/user'

import { faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { DOMAINS } from 'src/modules/auth/auth.constants'
import { ReactComponent as CollaboratorsSVG } from '../../assets/collaborators.svg'
import { useToggleHotDropdown } from '../../hooks/use-toggle-hot-dropdown'
import { useResponsiveHotDropdown } from '../../hooks/use-responsive-hot-dropdown'
import { ButtonGotoCollaboratorsArea } from './collaborators-area-btn.component'
import { BtnPendingInvites } from './collaborators-area-btn-pending-invites.component'
import { HeaderAreasImanage } from './collaborators-area-header-areas-i-manage.component'
import './collaborators-area.styles.scss'

const CollaboratorsArea = ({ tabIndex }) => {
  const { t } = useTranslation()
  const user = useSelector(state => state.user)
  const dispatch = useDispatch()
  const [isLoadingCollaboratorsAreas, setIsLoadingCollaboratorsAreas] = useState(false)
  const [hasPendingInvites, setHasPendingInvites] = useState(false)
  const hotDropdownRef = useRef<JSX.IntrinsicElements['hot-dropdown']>(null)

  const { state: isDropdownClosed, toggle } = useToggleHotDropdown(hotDropdownRef)

  useResponsiveHotDropdown(hotDropdownRef, {
    maxHeight: 768,
    isMobileFullscreen: true
  })

  useEffect(() => {
    const fetchCollaboratorsAreas = async () => {
      setIsLoadingCollaboratorsAreas(true)
      try {
        const [{ data: collaboratorsArea = [] }, pendingInvites] = await Promise.all([
          request(USER_REQUEST_IDENTIFIERS.COLLABORATOR),
          request(USER_REQUEST_IDENTIFIERS.COLLABORATOR_PENDING_INVITES)
        ])

        dispatch(userSlice.actions.updateUser({ collaboratorsArea }))
        setHasPendingInvites(pendingInvites)
      } catch (error) {
        console.error('BW: Failed fetching collaborator areas', error)
      } finally {
        setIsLoadingCollaboratorsAreas(false)
      }
    }

    if (!user.collaboratorsArea && !isLoadingCollaboratorsAreas) {
      fetchCollaboratorsAreas()
    }
  }, [user])

  function sendDataLayer() {
    const dataLayer = new DataLayer(
      EVENT.INTERACTION,
      CATEGORY.UNIVERSAL_BAR,
      'Click:CollaboratorsAreaMenu',
      'collaborator-areas'
    )

    GoogleAnalyticsDataLayer.sendPageInteraction(dataLayer)
  }

  function handleUserKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      toggle()

      /**
       * We have a delay in the dropdown state since
       * it is set in useEffect, the next tick;
       *
       * here we need to verify if the dropdown is open
       * (that is the state before the toggle function runs)
       */
      if (!isDropdownClosed) {
        sendDataLayer()
      }
    }
  }

  async function requestAttendantData(initialData) {
    Cookies.set('DATA_TO_BE_USED_AS_ATTENDANT', JSON.stringify(initialData), {
      domain: process.env.NODE_ENV === 'production' ? DOMAINS.production : DOMAINS.development
    })
    location.href = '/collaborators-dashboard'
  }

  const hasContent = (user.collaboratorsArea?.length && user.collaboratorsArea?.length > 0) || hasPendingInvites

  const shouldShowCollaboratorsArea = user?.entityType === 'LEGAL_ENTITY' || hasContent

  if (!user || !shouldShowCollaboratorsArea) {
    return null
  }

  return (
    <hot-dropdown
      ref={hotDropdownRef}
      slot="header-actions"
      class="header-collaborator-areas hot-dropdocked"
      tabIndex={tabIndex}
      onClick={isDropdownClosed ? sendDataLayer : void 0}
      onKeyDown={handleUserKeyDown}
    >
      <hot-menu-item slot="button" class="_cursor-pointer">
        <div className="hot-application-menu__icon">
          <hot-tooltip content={t('header.menu.general.collaborators_area')} position="bottom" class="_d-flex">
            <CollaboratorsSVG />
          </hot-tooltip>
        </div>
      </hot-menu-item>

      <div slot="menu" className="_text-gray-600" id="menu-collaborator-areas">
        <div className="_p-4">
          <div className="_d-flex _align-items-start _justify-content-between _mb-2">
            <ButtonGotoCollaboratorsArea />
            <div className="header-notification__close">
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </div>
          </div>

          <HeaderAreasImanage shouldShow={hasPendingInvites || user.collaboratorsArea?.length} />

          <BtnPendingInvites shouldShow={hasPendingInvites} />

          {user.collaboratorsArea?.map(collaboratorArea => (
            <button
              key={collaboratorArea.attendantId}
              className="header-collaborator-areas__item"
              onClick={() => requestAttendantData(collaboratorArea)}
            >
              <hot-avatar
                class="hot-avatar--sm hot-avatar--text _rounded-circle _mr-3"
                src={collaboratorArea.ownerPhotoUrl}
              >
                <span>{getNameInitials(collaboratorArea.ownerName)}</span>
              </hot-avatar>
              {collaboratorArea.ownerName}
            </button>
          ))}
        </div>
      </div>
    </hot-dropdown>
  )
}

export default React.memo(CollaboratorsArea)
